'use client';

import type { PrV2Version } from '@/data/v2/pr/types';
import type { PrV2ReviewResponse } from '@/lib/pr-v2/types';
import { useQueryState } from 'nuqs';
import { useCallback, useEffect, useMemo } from 'react';

const VERSION_BASE_PARAM_KEY = 'baseSha';
const VERSION_HEAD_PARAM_KEY = 'headSha';
const GITHUB_SHA_REGEX = /^[0-9a-f]{7,40}$/i;

type BuildPrV2VersionOptionsParams = {
  defaultBaseSha: string | null;
  defaultHeadSha: string | null;
  defaultBaseLabel: string;
  versions: PrV2Version[] | null | undefined;
};

export type PrV2VersionOption = {
  id: string;
  label: string;
  index: number;
  isDefaultBase: boolean;
  isDefaultHead: boolean;
};

type NormalizedComparison = {
  baseSha: string;
  headSha: string;
};

type ComparisonCandidate = {
  baseSha: string | null;
  headSha: string | null;
};

export type PrV2DefaultComparison = {
  baseSha: string | null;
  headSha: string | null;
  isAutoComparedToLastReview: boolean;
  lastReviewedVersionSha: string | null;
  lastReviewedSubmittedAt: string | null;
};

function normalizeSha(value: string): string {
  return value.trim().toLowerCase();
}

function normalizeLogin(value: string): string {
  return value.trim().toLowerCase();
}

function isValidSha(value: string | null | undefined): value is string {
  if (typeof value !== 'string') {
    return false;
  }

  return GITHUB_SHA_REGEX.test(value.trim());
}

function normalizeVersionNumber(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 1;
  }

  return Math.floor(value);
}

/**
 * Build the base + pushed-head timeline used by the v2 comparison picker.
 *
 * The base SHA is always index 1, then PR pushes in ascending order. We also
 * append the current PR head as a fallback in case version snapshots lag behind.
 */
export function buildPrV2VersionOptions({
  defaultBaseSha,
  defaultHeadSha,
  defaultBaseLabel,
  versions,
}: BuildPrV2VersionOptionsParams): PrV2VersionOption[] {
  if (!isValidSha(defaultBaseSha) || !isValidSha(defaultHeadSha)) {
    return [];
  }

  const optionsBySha = new Map<string, PrV2VersionOption>();
  optionsBySha.set(defaultBaseSha, {
    id: defaultBaseSha,
    label: defaultBaseLabel,
    index: 1,
    isDefaultBase: true,
    isDefaultHead: false,
  });

  (versions ?? []).forEach((version) => {
    if (!isValidSha(version.headSha) || optionsBySha.has(version.headSha)) {
      return;
    }

    const normalizedVersion = normalizeVersionNumber(version.version);
    // Use insertion sequence for ordering so duplicate `version` values remain distinct/comparable.
    const nextIndex = optionsBySha.size + 1;
    optionsBySha.set(version.headSha, {
      id: version.headSha,
      label: `v${normalizedVersion}`,
      index: nextIndex,
      isDefaultBase: false,
      isDefaultHead: false,
    });
  });

  if (!optionsBySha.has(defaultHeadSha)) {
    const nextIndex = Math.max(...Array.from(optionsBySha.values(), (option) => option.index)) + 1;
    optionsBySha.set(defaultHeadSha, {
      id: defaultHeadSha,
      label: 'Latest',
      index: nextIndex,
      isDefaultBase: false,
      isDefaultHead: true,
    });
  }

  const options = Array.from(optionsBySha.values()).sort((left, right) => left.index - right.index);
  return options.map((option) => ({
    ...option,
    isDefaultBase: option.id === defaultBaseSha,
    isDefaultHead: option.id === defaultHeadSha,
  }));
}

export function findOptionBySha(
  options: PrV2VersionOption[],
  sha: string | null,
): PrV2VersionOption | null {
  if (!sha) {
    return null;
  }

  const normalized = normalizeSha(sha);
  return options.find((option) => normalizeSha(option.id) === normalized) ?? null;
}

export function findPrV2PreviousPushComparison(
  versionOptions: PrV2VersionOption[],
): { baseSha: string; headSha: string } | null {
  const latestHeadOption =
    versionOptions.find((option) => option.isDefaultHead) ?? versionOptions.at(-1) ?? null;
  if (!latestHeadOption) {
    return null;
  }

  const previousPushOption =
    versionOptions
      .filter((option) => !option.isDefaultBase && option.index < latestHeadOption.index)
      .at(-1) ?? null;
  if (!previousPushOption) {
    return null;
  }

  return {
    baseSha: previousPushOption.id,
    headSha: latestHeadOption.id,
  };
}

function sanitizeComparisonSha(
  value: string | null,
  optionsByNormalizedSha: ReadonlyMap<string, PrV2VersionOption>,
): string | null {
  if (!isValidSha(value)) {
    return null;
  }

  const normalized = normalizeSha(value);
  const matchedOption = optionsByNormalizedSha.get(normalized);
  if (!matchedOption) {
    return null;
  }

  return matchedOption.id;
}

function resolveNormalizedComparison(
  options: PrV2VersionOption[],
  defaultBaseSha: string,
  defaultHeadSha: string,
  baseCandidate: string | null,
  headCandidate: string | null,
): NormalizedComparison {
  const optionsByNormalizedSha = new Map(
    options.map((option) => [normalizeSha(option.id), option] as const),
  );
  const defaultBase = optionsByNormalizedSha.get(normalizeSha(defaultBaseSha));
  const defaultHead = optionsByNormalizedSha.get(normalizeSha(defaultHeadSha));
  const fallbackHead = options[options.length - 1] ?? null;

  if (!defaultBase || !defaultHead || !fallbackHead) {
    return {
      baseSha: defaultBaseSha,
      headSha: defaultHeadSha,
    };
  }

  let base =
    (baseCandidate ? optionsByNormalizedSha.get(normalizeSha(baseCandidate)) : null) ?? defaultBase;
  let head =
    (headCandidate ? optionsByNormalizedSha.get(normalizeSha(headCandidate)) : null) ?? defaultHead;

  if (head.index <= base.index) {
    const firstNewerThanBase = options.find((option) => option.index > base.index);
    if (firstNewerThanBase) {
      head = firstNewerThanBase;
    } else {
      // The selected base is the newest known version, so we reset to the PR default pair.
      base = defaultBase;
      head = defaultHead.index > defaultBase.index ? defaultHead : fallbackHead;
    }
  }

  return {
    baseSha: base.id,
    headSha: head.id,
  };
}

function getSubmittedAtTime(submittedAt: string | null): number {
  if (!submittedAt) {
    return 0;
  }

  const timestamp = new Date(submittedAt).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function areComparisonCandidatesEqual(
  left: ComparisonCandidate | null | undefined,
  right: ComparisonCandidate | null | undefined,
): boolean {
  return left?.baseSha === right?.baseSha && left?.headSha === right?.headSha;
}

function buildDefaultComparisonResult({
  baseSha,
  headSha,
  isAutoComparedToLastReview,
  lastReviewedVersionSha = null,
  lastReviewedSubmittedAt = null,
}: {
  baseSha: string | null;
  headSha: string | null;
  isAutoComparedToLastReview: boolean;
  lastReviewedVersionSha?: string | null;
  lastReviewedSubmittedAt?: string | null;
}): PrV2DefaultComparison {
  return {
    baseSha,
    headSha,
    isAutoComparedToLastReview,
    lastReviewedVersionSha,
    lastReviewedSubmittedAt,
  };
}

/**
 * Resolve the v2 default compare pair for the current viewer.
 * We only auto-switch to "last reviewed -> latest" when the review commit still
 * exists in the current PR version timeline and is older than the current head.
 */
export function calculatePrV2DefaultComparison({
  defaultBaseSha,
  defaultHeadSha,
  defaultBaseLabel = 'Base',
  versions,
  reviews,
  currentUserLogin,
}: {
  defaultBaseSha: string | null;
  defaultHeadSha: string | null;
  defaultBaseLabel?: string;
  versions: PrV2Version[] | null | undefined;
  reviews: PrV2ReviewResponse[] | null | undefined;
  currentUserLogin?: string | null;
}): PrV2DefaultComparison {
  if (!defaultBaseSha || !defaultHeadSha) {
    return buildDefaultComparisonResult({
      baseSha: defaultBaseSha,
      headSha: defaultHeadSha,
      isAutoComparedToLastReview: false,
    });
  }

  const normalizedCurrentUserLogin =
    typeof currentUserLogin === 'string' ? normalizeLogin(currentUserLogin) : '';
  if (!normalizedCurrentUserLogin) {
    return buildDefaultComparisonResult({
      baseSha: defaultBaseSha,
      headSha: defaultHeadSha,
      isAutoComparedToLastReview: false,
    });
  }

  const versionOptions = buildPrV2VersionOptions({
    defaultBaseSha,
    defaultHeadSha,
    defaultBaseLabel,
    versions,
  });
  const defaultHeadOption = findOptionBySha(versionOptions, defaultHeadSha);
  if (!defaultHeadOption) {
    return buildDefaultComparisonResult({
      baseSha: defaultBaseSha,
      headSha: defaultHeadSha,
      isAutoComparedToLastReview: false,
    });
  }

  const latestSubmittedReview = (reviews ?? [])
    .filter((review) => {
      if (!review.submittedAt || !review.commitId || !review.userLogin) {
        return false;
      }

      return normalizeLogin(review.userLogin) === normalizedCurrentUserLogin;
    })
    .sort(
      (left, right) => getSubmittedAtTime(right.submittedAt) - getSubmittedAtTime(left.submittedAt),
    )[0];

  if (!latestSubmittedReview?.commitId) {
    return buildDefaultComparisonResult({
      baseSha: defaultBaseSha,
      headSha: defaultHeadSha,
      isAutoComparedToLastReview: false,
    });
  }

  const reviewedOption = findOptionBySha(versionOptions, latestSubmittedReview.commitId);
  if (!reviewedOption) {
    return buildDefaultComparisonResult({
      baseSha: defaultBaseSha,
      headSha: defaultHeadSha,
      isAutoComparedToLastReview: false,
    });
  }

  if (
    reviewedOption.id === defaultBaseSha ||
    reviewedOption.id === defaultHeadSha ||
    reviewedOption.index >= defaultHeadOption.index
  ) {
    return buildDefaultComparisonResult({
      baseSha: defaultBaseSha,
      headSha: defaultHeadSha,
      isAutoComparedToLastReview: false,
      lastReviewedVersionSha: reviewedOption.id,
      lastReviewedSubmittedAt: latestSubmittedReview.submittedAt,
    });
  }

  return buildDefaultComparisonResult({
    baseSha: reviewedOption.id,
    headSha: defaultHeadSha,
    isAutoComparedToLastReview: true,
    lastReviewedVersionSha: reviewedOption.id,
    lastReviewedSubmittedAt: latestSubmittedReview.submittedAt,
  });
}

export function usePrV2VersionComparison({
  defaultBaseSha,
  defaultHeadSha,
  defaultBaseLabel = 'Base',
  autoComparison = null,
  versions,
}: {
  defaultBaseSha: string | null;
  defaultHeadSha: string | null;
  defaultBaseLabel?: string;
  autoComparison?: ComparisonCandidate | null;
  versions: PrV2Version[] | null | undefined;
}) {
  const [baseParam, setBaseParam] = useQueryState(VERSION_BASE_PARAM_KEY, {
    history: 'replace',
    clearOnDefault: true,
  });
  const [headParam, setHeadParam] = useQueryState(VERSION_HEAD_PARAM_KEY, {
    history: 'replace',
    clearOnDefault: true,
  });

  const versionOptions = useMemo(
    () =>
      buildPrV2VersionOptions({
        defaultBaseSha,
        defaultHeadSha,
        defaultBaseLabel,
        versions,
      }),
    [defaultBaseLabel, defaultBaseSha, defaultHeadSha, versions],
  );
  const optionsByNormalizedSha = useMemo(
    () => new Map(versionOptions.map((option) => [normalizeSha(option.id), option] as const)),
    [versionOptions],
  );

  const normalizedBaseParam = isValidSha(baseParam) ? normalizeSha(baseParam) : null;
  const normalizedHeadParam = isValidSha(headParam) ? normalizeSha(headParam) : null;
  const normalizedDefaultBaseSha = isValidSha(defaultBaseSha) ? normalizeSha(defaultBaseSha) : null;
  const normalizedDefaultHeadSha = isValidSha(defaultHeadSha) ? normalizeSha(defaultHeadSha) : null;
  const normalizedAutoComparison = useMemo((): NormalizedComparison | null => {
    if (!defaultBaseSha || !defaultHeadSha || versionOptions.length === 0) {
      return null;
    }

    const sanitizedAutoBaseCandidate = sanitizeComparisonSha(
      autoComparison?.baseSha ?? null,
      optionsByNormalizedSha,
    );
    const sanitizedAutoHeadCandidate = sanitizeComparisonSha(
      autoComparison?.headSha ?? null,
      optionsByNormalizedSha,
    );

    if (!sanitizedAutoBaseCandidate && !sanitizedAutoHeadCandidate) {
      return null;
    }

    return resolveNormalizedComparison(
      versionOptions,
      defaultBaseSha,
      defaultHeadSha,
      sanitizedAutoBaseCandidate,
      sanitizedAutoHeadCandidate,
    );
  }, [
    autoComparison?.baseSha,
    autoComparison?.headSha,
    defaultBaseSha,
    defaultHeadSha,
    optionsByNormalizedSha,
    versionOptions,
  ]);
  const hasComparisonParams = Boolean(baseParam || headParam);
  const hasCustomComparisonParams = Boolean(
    (normalizedBaseParam &&
      normalizedBaseParam !== normalizedDefaultBaseSha &&
      normalizedBaseParam !== normalizedDefaultHeadSha) ||
    (normalizedHeadParam &&
      normalizedHeadParam !== normalizedDefaultHeadSha &&
      normalizedHeadParam !== normalizedDefaultBaseSha),
  );
  // Preserve deep-linked custom comparison params until versions are loaded.
  const shouldDeferComparisonResolution = versions === undefined && hasCustomComparisonParams;

  const canResolveComparison = Boolean(
    isValidSha(defaultBaseSha) &&
    isValidSha(defaultHeadSha) &&
    versionOptions.length > 0 &&
    !shouldDeferComparisonResolution,
  );

  const normalizedComparison = useMemo((): NormalizedComparison | null => {
    if (!canResolveComparison || !defaultBaseSha || !defaultHeadSha) {
      return null;
    }

    const sanitizedBaseCandidate = sanitizeComparisonSha(baseParam, optionsByNormalizedSha);
    const sanitizedHeadCandidate = sanitizeComparisonSha(headParam, optionsByNormalizedSha);
    const sanitizedAutoBaseCandidate = sanitizeComparisonSha(
      autoComparison?.baseSha ?? null,
      optionsByNormalizedSha,
    );
    const sanitizedAutoHeadCandidate = sanitizeComparisonSha(
      autoComparison?.headSha ?? null,
      optionsByNormalizedSha,
    );

    const baseCandidate = hasComparisonParams ? sanitizedBaseCandidate : sanitizedAutoBaseCandidate;
    const headCandidate = hasComparisonParams ? sanitizedHeadCandidate : sanitizedAutoHeadCandidate;

    return resolveNormalizedComparison(
      versionOptions,
      defaultBaseSha,
      defaultHeadSha,
      baseCandidate,
      headCandidate,
    );
  }, [
    autoComparison?.baseSha,
    autoComparison?.headSha,
    baseParam,
    canResolveComparison,
    defaultBaseSha,
    defaultHeadSha,
    hasComparisonParams,
    headParam,
    optionsByNormalizedSha,
    versionOptions,
  ]);

  const effectiveBaseSha = normalizedComparison?.baseSha ?? defaultBaseSha;
  const effectiveHeadSha = normalizedComparison?.headSha ?? defaultHeadSha;

  const isCustomComparison = Boolean(
    canResolveComparison &&
    normalizedComparison &&
    (normalizedComparison.baseSha !== defaultBaseSha ||
      normalizedComparison.headSha !== defaultHeadSha),
  );
  const isAutoComparisonActive = Boolean(
    canResolveComparison &&
    normalizedComparison &&
    !hasComparisonParams &&
    normalizedComparison.baseSha !== defaultBaseSha &&
    normalizedComparison.headSha === defaultHeadSha,
  );
  const shouldPreserveExplicitDefaultOverride = Boolean(
    hasComparisonParams &&
    normalizedComparison &&
    defaultBaseSha &&
    defaultHeadSha &&
    normalizedComparison.baseSha === defaultBaseSha &&
    normalizedComparison.headSha === defaultHeadSha &&
    normalizedAutoComparison &&
    !areComparisonCandidatesEqual(normalizedAutoComparison, {
      baseSha: defaultBaseSha,
      headSha: defaultHeadSha,
    }),
  );
  const hasUserComparisonOverride = Boolean(hasComparisonParams);
  const hasMeaningfulComparison = versionOptions.length >= 3;

  // Keep URL query params canonical so shared links always point to a valid pair.
  useEffect(() => {
    if (
      !hasComparisonParams ||
      !canResolveComparison ||
      !normalizedComparison ||
      !defaultBaseSha ||
      !defaultHeadSha
    ) {
      return;
    }

    const canonicalBaseParam = shouldPreserveExplicitDefaultOverride
      ? defaultBaseSha
      : normalizedComparison.baseSha === defaultBaseSha
        ? null
        : normalizedComparison.baseSha;
    const canonicalHeadParam = shouldPreserveExplicitDefaultOverride
      ? defaultHeadSha
      : normalizedComparison.headSha === defaultHeadSha
        ? null
        : normalizedComparison.headSha;

    if (baseParam !== canonicalBaseParam) {
      void setBaseParam(canonicalBaseParam);
    }
    if (headParam !== canonicalHeadParam) {
      void setHeadParam(canonicalHeadParam);
    }
  }, [
    baseParam,
    canResolveComparison,
    defaultBaseSha,
    defaultHeadSha,
    hasComparisonParams,
    headParam,
    normalizedComparison,
    shouldPreserveExplicitDefaultOverride,
    setBaseParam,
    setHeadParam,
  ]);

  const setComparison = useCallback(
    (nextComparison: NormalizedComparison | null) => {
      if (!canResolveComparison || !defaultBaseSha || !defaultHeadSha || !nextComparison) {
        void setBaseParam(null);
        void setHeadParam(null);
        return;
      }

      const baseOption = findOptionBySha(versionOptions, nextComparison.baseSha);
      const headOption = findOptionBySha(versionOptions, nextComparison.headSha);
      if (!baseOption || !headOption) {
        return;
      }

      const normalized = resolveNormalizedComparison(
        versionOptions,
        defaultBaseSha,
        defaultHeadSha,
        baseOption.id,
        headOption.id,
      );

      const shouldPersistDefaultOverride =
        normalizedAutoComparison &&
        normalized.baseSha === defaultBaseSha &&
        normalized.headSha === defaultHeadSha &&
        !areComparisonCandidatesEqual(normalizedAutoComparison, normalized);

      void setBaseParam(
        shouldPersistDefaultOverride
          ? defaultBaseSha
          : normalized.baseSha === defaultBaseSha
            ? null
            : normalized.baseSha,
      );
      void setHeadParam(
        shouldPersistDefaultOverride
          ? defaultHeadSha
          : normalized.headSha === defaultHeadSha
            ? null
            : normalized.headSha,
      );
    },
    [
      canResolveComparison,
      defaultBaseSha,
      defaultHeadSha,
      normalizedAutoComparison,
      setBaseParam,
      setHeadParam,
      versionOptions,
    ],
  );

  const resetComparison = useCallback(() => {
    void setBaseParam(null);
    void setHeadParam(null);
  }, [setBaseParam, setHeadParam]);

  return {
    versionOptions,
    effectiveBaseSha,
    effectiveHeadSha,
    isCustomComparison,
    isAutoComparisonActive,
    hasUserComparisonOverride,
    hasMeaningfulComparison,
    setComparison,
    resetComparison,
  };
}
